import React from "react"
import { useTranslation } from "react-i18next"
import {
  faCircleQuestion,
  faCreditCard,
  faLink,
  faMoneyBillTransfer,
  faPlug,
  faReceipt,
  faShieldHalved,
  faTruckFast,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { usePublicContact } from "../../context/usePublicContactContext"
import { ContractDownloadsCard } from "../../components/ContractDownloads/ContractDownloadsCard"

const faqList = ["faq_connect", "faq_payment_delay", "faq_split", "faq_billing_error", "faq_security"] as const

const paymentSteps = ["step_access", "step_connect", "step_validate", "step_enable"] as const

export const HelpCenterPage: React.FC = () => {
  const { t } = useTranslation("helpCenter")

  const { contact } = usePublicContact()

  return (
    <Animate type={AnimationTypes.fadeInTraslate}>
      <div className="flex h-[calc(100%-25px)] w-full flex-col overflow-y-auto p-4 md:p-2">
        <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
          <section className="rounded-2xl border border-red-100 bg-gradient-to-r from-red-50 to-orange-50 p-6 shadow-sm">
            <span className="inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-white">
              <FontAwesomeIcon icon={faCircleQuestion} />
              {t("label_badge")}
            </span>

            <h1 className="mt-4 text-2xl font-black text-slate-900 sm:text-3xl">{t("label_title")}</h1>

            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600 sm:text-base">
              {t("label_description")}
            </p>
          </section>
        </Animate>

        <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s">
          <section className="mt-4 rounded-2xl border border-red-200 bg-white p-6 shadow-sm">
            <p className="text-sm font-bold text-red-700">{t("label_disclaimer_title")}</p>

            <p className="mt-2 text-sm leading-relaxed text-slate-700">{t("label_disclaimer_text")}</p>
          </section>
        </Animate>

        <div className="mt-4">
          <ContractDownloadsCard />
        </div>

        <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
          <section className="mt-4 grid gap-4 lg:grid-cols-3">
            <Animate type={AnimationTypes.slideRightIn} duration="2s">
              <article className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-600">
                  <FontAwesomeIcon icon={faPlug} />
                </div>

                <h2 className="text-base font-extrabold text-slate-900">{t("card_mp_title")}</h2>

                <p className="mt-2 text-sm text-slate-600">{t("card_mp_description")}</p>
              </article>
            </Animate>

            <Animate type={AnimationTypes.slideRightIn} duration="2s">
              <article className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-red-100 text-red-600">
                  <FontAwesomeIcon icon={faMoneyBillTransfer} />
                </div>

                <h2 className="text-base font-extrabold text-slate-900">{t("card_split_title")}</h2>

                <p className="mt-2 text-sm text-slate-600">{t("card_split_description")}</p>
              </article>
            </Animate>

            <Animate type={AnimationTypes.slideRightIn} duration="2s">
              <article className="rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-700">
                  <FontAwesomeIcon icon={faReceipt} />
                </div>

                <h2 className="text-base font-extrabold text-slate-900">{t("card_reconcile_title")}</h2>

                <p className="mt-2 text-sm text-slate-600">{t("card_reconcile_description")}</p>
              </article>
            </Animate>
          </section>
        </Animate>

        <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
          <section className="mt-4 grid gap-4 xl:grid-cols-2">
            <article className="rounded-2xl border border-red-100 bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-extrabold text-slate-900">{t("label_mp_flow_title")}</h3>

              <ol className="space-y-3">
                {paymentSteps.map((step, index) => (
                  <Animate key={index} type={AnimationTypes.slideRightIn} duration="2s">
                    <li key={step} className="flex gap-3 rounded-xl border border-orange-100 bg-orange-50/40 p-3">
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-linear-to-r from-red-600 to-orange-500 text-xs font-bold text-white">
                        {index + 1}
                      </span>

                      <div>
                        <p className="text-sm font-bold text-slate-800">{t(`${step}.title`)}</p>
                        <p className="text-xs text-slate-600 sm:text-sm">{t(`${step}.description`)}</p>
                      </div>
                    </li>
                  </Animate>
                ))}
              </ol>
            </article>

            <article className="rounded-2xl border border-red-100 bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-extrabold text-slate-900">{t("label_faq_title")}</h3>

              <div className="space-y-3">
                {faqList.map((item, index) => (
                  <Animate key={index} type={AnimationTypes.slideRightIn} duration="2s">
                    <details
                      key={item}
                      className="group rounded-xl border border-orange-100 bg-orange-50/40 px-4 py-3 transition-colors open:border-red-200 open:bg-white"
                    >
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold text-slate-800">
                        {t(`${item}.question`)}
                        <span className="text-red-500 transition-transform group-open:rotate-45">+</span>
                      </summary>

                      <p className="mt-2 text-xs leading-relaxed text-slate-600 sm:text-sm">{t(`${item}.answer`)}</p>
                    </details>
                  </Animate>
                ))}
              </div>
            </article>
          </section>
        </Animate>

        <Animate type={AnimationTypes.fadeInTraslate} duration="2.2s">
          <section className="mt-4 rounded-2xl border border-orange-100 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-extrabold text-slate-900">{t("label_links_title")}</h3>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <a
                href="https://www.mercadopago.com.br/developers/pt/docs"
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-xl border border-orange-100 bg-orange-50/40 px-4 py-3 text-sm font-semibold text-slate-800 hover:border-red-200 hover:bg-white"
                data-interaction-description-key="interactionDescriptions:pages.myRestaurant.helpCenterPage.link.linkDocs"
              >
                {t("label_link_docs")}
                <FontAwesomeIcon icon={faLink} className="text-red-500" />
              </a>

              <a
                href="https://www.mercadopago.com.br/developers/pt/docs/checkout-api/overview"
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-xl border border-orange-100 bg-orange-50/40 px-4 py-3 text-sm font-semibold text-slate-800 hover:border-red-200 hover:bg-white"
                data-interaction-description-key="interactionDescriptions:pages.myRestaurant.helpCenterPage.link.linkCheckout"
              >
                {t("label_link_checkout")}
                <FontAwesomeIcon icon={faLink} className="text-red-500" />
              </a>

              <a
                href="https://www.mercadopago.com.br/developers/pt/docs/checkout-api/payment-management/split-payments"
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-xl border border-orange-100 bg-orange-50/40 px-4 py-3 text-sm font-semibold text-slate-800 hover:border-red-200 hover:bg-white"
                data-interaction-description-key="interactionDescriptions:pages.myRestaurant.helpCenterPage.link.linkSplit"
              >
                {t("label_link_split")}
                <FontAwesomeIcon icon={faLink} className="text-red-500" />
              </a>

              <a
                href="https://www.mercadopago.com.br/ajuda"
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-xl border border-orange-100 bg-orange-50/40 px-4 py-3 text-sm font-semibold text-slate-800 hover:border-red-200 hover:bg-white"
                data-interaction-description-key="interactionDescriptions:pages.myRestaurant.helpCenterPage.link.linkMpHelp"
              >
                {t("label_link_mp_help")}
                <FontAwesomeIcon icon={faLink} className="text-red-500" />
              </a>
            </div>
          </section>
        </Animate>

        <Animate type={AnimationTypes.fadeInTraslate} duration="2.5s">
          <section className="mt-4 rounded-2xl border border-orange-100 bg-white p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-red-100 text-red-600">
                <FontAwesomeIcon icon={faCreditCard} />
              </span>

              <div>
                <h3 className="text-lg font-extrabold text-slate-900">{t("label_contact_title")}</h3>

                <p className="mt-1 text-sm text-slate-600">{t("label_contact_description")}</p>

                {contact.email && (
                  <p className="mt-3 text-sm font-semibold text-slate-800">
                    {t("label_contact_mail")}: {contact.email}
                  </p>
                )}

                {contact.whatsappLabel && (
                  <p className="text-sm font-semibold text-slate-800">
                    {t("label_contact_whatsapp")}: {contact.whatsappLabel}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-orange-100 bg-orange-50/70 p-4 text-sm text-slate-700">
              <p className="font-bold text-slate-900">
                <FontAwesomeIcon className="mr-2 text-orange-500" icon={faTruckFast} />
                {t("label_courier_note_title")}
              </p>

              <p className="mt-1">{t("label_courier_note_description")}</p>
            </div>

            <div className="mt-4 rounded-xl border border-red-100 bg-red-50/70 p-4 text-sm text-slate-700">
              <p className="font-bold text-slate-900">
                <FontAwesomeIcon className="mr-2 text-red-500" icon={faShieldHalved} />
                {t("label_tip_title")}
              </p>

              <p className="mt-1">{t("label_tip_description")}</p>
            </div>
          </section>
        </Animate>
      </div>
    </Animate>
  )
}
